import { computed, defineComponent, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { User } from '@/store/user/user.model';
import { UserSchema } from '@/store/user/user.validator';
import { UserAction, UserGetter } from '@/store/user/user.module';
import { Role } from '@/store/role/role.model';
import { RoleAction, RoleGetter } from '@/store/role/role.module';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';
import Multiselect from '@vueform/multiselect';

export default defineComponent({
  name: 'UserFormComponent',
  components: {
    BaseModalComponent,
    Multiselect,
  },
  props: {
    userId: {
      type: String,
    }
  },
  setup: (props) => {
    const store = useStore();
    const isEditMode = computed(() => props.userId !== '');
    const user = computed<User | undefined>(() => store.getters[UserGetter.BY_ID](props.userId));
    const title = computed(() => isEditMode.value ? 'แก้ไขผู้ใช้งาน' : 'เพิ่มผู้ใช้งาน');
    const userForm = ref<InstanceType<typeof BaseModalComponent>>();
    const roles = computed<Role[]>(() => store.getters[RoleGetter.ALL]);
    const roleOptions = computed<string[]>(() => roles.value.map(m => m.name));

    // form
    interface Form {
      id?: string | null;
      userName: string;
      password: string;
      firstName: string;
      lastName: string;
      email: string;
      roles: string[];
      isActive: boolean;
    }
    const formData = reactive<Form>({
      id: null,
      userName: '',
      password: 'welcome',
      firstName: '',
      lastName: '',
      email: '',
      roles: [],
      isActive: true,
    });
    const validator = computed(() => UserSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      userName: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'userName')),
      firstName: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'firstName')),
      lastName: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'lastName')),
      email: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'email')),
      roles: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'roles')),
    });
    const isValid = computed(() => !errors.userName && !errors.firstName && !errors.lastName && !errors.email && !errors.roles);
    const isLoading = computed<boolean>(() => store.getters[UserGetter.STATUS] === 'init' || store.getters[UserGetter.STATUS] === 'loading');
    const submited = ref(false);

    onMounted(() => {
      store.dispatch(RoleAction.FETCH);
    });

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        if (isEditMode.value) {
          formData.id = props.userId;
          formData.userName = user.value?.userName ?? '';
          formData.firstName = user.value?.firstName ?? '';
          formData.lastName = user.value?.lastName ?? '';
          formData.email = user.value?.email ?? '';
          formData.roles = user.value ? user.value?.roles.map(r => r.name) : [];
          formData.isActive = user.value?.isActive ?? false;
        }
        resolve();
      })
      .then(() => userForm.value?.show());
    };

    // hide modal
    const hide = () => {
      userForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.userName = '';
        formData.firstName = '';
        formData.lastName = '';
        formData.email = '';
        formData.roles = [];
        formData.isActive = true;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(UserAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(UserAction.CREATE, formData)
            .then(() => hide());
        }
      }
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      userForm,
      roleOptions,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm
    };
  },
});
